<template>
  <div class="assignment-toolbar">
    <div class="md-layout md-gutter mt-2">
      <!-- Schedule Selection Section -->
      <div class="md-layout-item md-small-size-33 md-xsmall-size-100">
        <div v-if="hasSchedules">
          <div class="md-layout-item md-small-size-100">
            <select-input
                v-model="localSchedule"
                label="Select Schedule"
                :options="schedules"
                :error="apiValidationErrors.schedule"
                :track-by="'shift_date'"
                :name="'shift_date'"
                :taggable="false"
                :reduce-to-value="false"
                :disabled="isGenerating"
                @selecting="emitSchedule"
            />
          </div>
        </div>
        <div v-else>
          <md-button
              @click="handleGenerateSchedule"
              class="md-raised md-primary"
              :disabled="isGenerating">
            <md-icon v-if="isGenerating">schedule</md-icon>
            <span>{{ isGenerating ? 'Generating...' : 'Generate Schedules' }}</span>
          </md-button>
        </div>
      </div>

      <!-- Shift Selection Section -->
      <div class="md-layout-item md-small-size-33 md-xsmall-size-100">
        <select-input
            v-model="localShift"
            label="Workplace/Work Point"
            :options="filteredShifts"
            :error="apiValidationErrors.department_id"
            :track-by="'id'"
            :name="'name'"
            :taggable="false"
            :reduce-to-value="false"
            :disabled="!hasSchedules || isGenerating"
            @selecting="emitShift"
        />
        <div v-if="!hasSchedules" class="help-text">
          Please generate schedules first
        </div>
      </div>

      <!-- Action Buttons Section -->
      <div class="md-layout-item md-small-size-33 md-xsmall-size-100">
        <div class="button-group">
          <md-button
              @click="handleViewTakeOn"
              class="md-raised md-primary mt-1"
              :disabled="!canViewTakeOn || isGenerating">
            <md-icon>visibility</md-icon>
            View Take On
          </md-button>

          <md-button
              @click="handleGoBack"
              class="md-raised md-secondary mt-1"
              :disabled="isGenerating">
            <md-icon>arrow_back</md-icon>
            Go Back
          </md-button>
        </div>
      </div>
    </div>

    <!-- Shift Details Card -->
    <div class="card card-body shift-details" v-if="showShiftDetails">
      <div class="shift-details-header">
        <h5 class="shift-title">{{ localShift.name }}</h5>
        <md-badge class="md-accent" :md-content="getStatusLabel(localShift)">
          {{ getStatusLabel(localShift) }}
        </md-badge>
      </div>

      <items-view>
        <item-view label="Shift Date" :value="formatDate(localSchedule.shift_date)">
          <md-icon slot="icon">event</md-icon>
        </item-view>

        <item-view label="Shift Name" :value="localShift.name">
          <md-icon slot="icon">business_center</md-icon>
        </item-view>

        <item-view label="Start Time" :value="formatTime(localShift.start_time)">
          <md-icon slot="icon">access_time</md-icon>
        </item-view>

        <item-view label="End Time" :value="formatTime(localShift.end_time)">
          <md-icon slot="icon">access_time</md-icon>
        </item-view>

        <item-view
            label="Cross Day"
            :value="localShift.cross_day ? 'Yes' : 'No'"
            :class="localShift.cross_day ? 'text-warning' : 'text-success'">
          <md-icon slot="icon">wb_sunny</md-icon>
        </item-view>

        <item-view
            label="Schedule Count"
            :value="localSchedule.labourer_count || 0">
          <md-icon slot="icon">people</md-icon>
        </item-view>

        <item-view
            v-if="localShift.description"
            label="Description"
            :value="localShift.description">
          <md-icon slot="icon">description</md-icon>
        </item-view>
      </items-view>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isGenerating" class="loading-overlay">
      <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
    </div>
  </div>
</template>

<script>
import SelectInput from '@/components/Inputs/SelectInput.vue';
import formMixin from '@/mixins/form-mixin';
import ItemsView from '@/components/Inputs/ItemsView.vue';
import ItemView from '@/components/Inputs/ItemView.vue';
import Swal from "sweetalert2";

export default {
  name: 'AssignmentToolbar',
  components: {
    ItemView,
    ItemsView,
    SelectInput
  },
  mixins: [formMixin],
  props: {
    schedule: {
      type: Object,
      required: true,
      default: function() {
        return {};
      }
    },
    shift: {
      type: Object,
      required: true,
      default: function() {
        return {};
      }
    },
    schedules: {
      type: Array,
      default: function() {
        return [];
      }
    },
    shifts: {
      type: Array,
      default: function() {
        return [];
      }
    },
    isGenerating: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localSchedule: this.cloneObject(this.schedule),
      localShift: this.cloneObject(this.shift)
    };
  },
  computed: {
    hasSchedules() {
      return this.schedules && this.schedules.length > 0;
    },

    filteredShifts() {
      if (!this.shifts || this.shifts.length === 0) {
        return [];
      }

      // Filter out inactive shifts if needed
      return this.shifts.filter(function(shift) {
        return shift.active !== false;
      });
    },

    canViewTakeOn() {
      return this.localSchedule &&
          this.localSchedule.shift_date &&
          this.localShift &&
          this.localShift.id &&
          !this.isGenerating;
    },

    showShiftDetails() {
      return this.localShift &&
          this.localShift.name &&
          this.localSchedule &&
          this.localSchedule.shift_date;
    }
  },
  watch: {
    schedule: {
      handler: function(newVal) {
        this.localSchedule = this.cloneObject(newVal);
      },
      deep: true,
      immediate: true
    },

    shift: {
      handler: function(newVal) {
        this.localShift = this.cloneObject(newVal);
      },
      deep: true,
      immediate: true
    },

    // Auto-trigger view take on when both schedule and shift are selected
    canViewTakeOn: {
      handler: function(newVal, oldVal) {
        if (newVal && !oldVal && !this.isGenerating) {
          // Auto-generate takeon when both are selected for the first time
          this.$emit('view:take:on');
        }
      }
    }
  },
  methods: {
    cloneObject(obj) {
      if (!obj || typeof obj !== 'object') {
        return {};
      }
      return JSON.parse(JSON.stringify(obj));
    },

    emitShift(value) {
      if (!value || !value.id) {
        return;
      }

      this.localShift = value;
      this.$emit('update:shift', this.localShift);

      // Clear schedule validation error when shift changes
      if (this.apiValidationErrors.department_id) {
        this.apiValidationErrors.department_id = null;
      }

      // Notify parent of successful selection
      this.$store.dispatch('alerts/info', 'Shift selected: ' + value.name);
    },

    emitSchedule(value) {
      if (!value || !value.shift_date) {
        return;
      }

      this.localSchedule = value;
      this.$emit('update:schedule', this.localSchedule);

      // Clear schedule validation error
      if (this.apiValidationErrors.schedule) {
        this.apiValidationErrors.schedule = null;
      }

      // Notify parent of successful selection
      this.$store.dispatch('alerts/info', 'Schedule selected: ' + this.formatDate(value.shift_date));
    },

    handleGenerateSchedule() {
      if (this.isGenerating) {
        return;
      }

      // Confirm before generating
      var self = this;
      Swal.fire({
        title: 'Generate Schedules?',
        text: 'This will create new schedule entries. Continue?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, generate',
        cancelButtonText: 'Cancel'
      }).then(function(result) {
        if (result.value) {
          self.$emit('generateSchedule');
        }
      });
    },

    handleViewTakeOn() {
      if (!this.canViewTakeOn) {
        var errorMessage = '';
        if (!this.localSchedule || !this.localSchedule.shift_date) {
          errorMessage = 'Please select a schedule first';
        } else if (!this.localShift || !this.localShift.id) {
          errorMessage = 'Please select a shift first';
        }

        if (errorMessage) {
          this.$store.dispatch('alerts/warning', errorMessage);
        }
        return;
      }

      this.$emit('view:take:on');
    },

    handleGoBack() {
      var self = this;

      // Confirm if there might be unsaved changes
      Swal.fire({
        title: 'Go Back?',
        text: 'Any unsaved changes will be lost. Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, go back',
        cancelButtonText: 'Stay'
      }).then(function(result) {
        if (result.value) {
          self.$emit('go:back');
        }
      });
    },

    formatDate(date) {
      if (!date) return 'N/A';

      var d = new Date(date);
      if (isNaN(d.getTime())) return date;

      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    formatTime(time) {
      if (!time) return 'N/A';

      // If time is in HH:MM:SS format
      if (typeof time === 'string' && time.match(/^\d{2}:\d{2}/)) {
        var parts = time.split(':');
        var date = new Date();
        date.setHours(parseInt(parts[0], 10));
        date.setMinutes(parseInt(parts[1], 10));
        return date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      return time;
    },

    getStatusLabel(shift) {
      if (!shift || !shift.start_time || !shift.end_time) {
        return 'Inactive';
      }

      // Simple status check - you can enhance this based on current time
      var now = new Date();
      var startTime = this.parseTimeToDate(shift.start_time);
      var endTime = this.parseTimeToDate(shift.end_time);

      if (shift.cross_day) {
        return 'Active (Cross Day)';
      }

      if (now >= startTime && now <= endTime) {
        return 'Active';
      }

      return 'Inactive';
    },

    parseTimeToDate(timeString) {
      if (!timeString) return new Date();

      var parts = timeString.split(':');
      var date = new Date();
      date.setHours(parseInt(parts[0], 10) || 0);
      date.setMinutes(parseInt(parts[1], 10) || 0);
      date.setSeconds(parseInt(parts[2], 10) || 0);
      return date;
    }
  }
};
</script>

<style scoped>
.assignment-toolbar {
  position: relative;
  padding: 16px;
  background: #fff;
  border-radius: 4px;
}

.mt-2 {
  margin-top: 8px;
}

.mt-1 {
  margin-top: 4px;
}

.md-card-content,
.md-layout,
.md-layout-item {
  overflow: visible !important;
  position: relative;
}

::v-deep .multiselect__content-wrapper {
  z-index: 9999 !important;
  position: absolute !important;
}

.button-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.button-group .md-button {
  flex: 1;
  min-width: 120px;
}

.button-group .md-button .md-icon {
  margin-right: 8px;
}

.shift-details {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  animation: slideIn 0.3s ease-out;
}

.shift-details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.shift-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.help-text {
  font-size: 12px;
  color: #ff9800;
  margin-top: 4px;
  font-style: italic;
}

.text-warning {
  color: #ff9800;
}

.text-success {
  color: #4caf50;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  border-radius: 4px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .assignment-toolbar {
    padding: 12px;
  }

  .button-group {
    flex-direction: column;
    gap: 8px;
  }

  .button-group .md-button {
    width: 100%;
    margin: 0;
  }

  .shift-details-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

/* Improved styling for items-view */
::v-deep .items-view {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

::v-deep .item-view {
  display: flex;
  align-items: center;
  padding: 8px;
  background: white;
  border-radius: 4px;
}

::v-deep .item-view .item-label {
  font-weight: 500;
  color: #666;
  min-width: 120px;
}

::v-deep .item-view .item-value {
  color: #333;
  font-weight: 500;
}

::v-deep .item-view .md-icon {
  margin-right: 8px;
  font-size: 20px;
  color: #9e9e9e;
}
</style>